<script setup lang="ts">
import { SHARE_IMAGE, SITE_URL } from '@@/constants/marketing'
import { contentPageTitle } from '@@/constants/pageMeta'

definePageMeta({
  layout: 'website',
})

const config = useRuntimeConfig()

if (!config.public.enableBlog) {
  throw createError({ statusCode: 404, statusMessage: 'Page Not Found' })
}

const route = useRoute()

const { data: article } = await useAsyncData<any>(`content-${route.path}`, async () => {
  return await queryCollection('blog').where('path', '=', route.path).first()
})

// A real 404 for unknown slugs; rendering the page shell would be a soft 404.
if (!article.value) {
  throw createError({ statusCode: 404, statusMessage: 'Post not found' })
}

const SECTION_PREFIX = '/blog/'

const { data: relatedArticles } = await useAsyncData<any[]>(
  `related-${route.path}`,
  async () => {
    if (!article.value) return []
    const all = await queryCollection('blog').all()
    const sameSection = all.filter(
      (post: any) => typeof post.path === 'string' && post.path.startsWith(SECTION_PREFIX) && post.path !== route.path
    )

    const tag = article.value?.meta?.tags?.[0]
    if (tag) {
      const tagged = sameSection.filter((post: any) => post.meta?.tags?.includes(tag)).slice(0, 3)
      if (tagged.length) return tagged
    }
    return sameSection.slice(0, 3)
  },
  { watch: [() => route.path] }
)

// Frontmatter `seo` overrides the visible title/description (Nuxt Content fills it
// from them otherwise). Checked by tests/seo/pageMeta.test.ts.
const title = computed(() => contentPageTitle(article.value?.seo?.title || article.value?.title || 'Blog'))
const description = computed(() => article.value?.seo?.description || article.value?.description || '')
usePageMeta(() => ({ title: title.value, description: description.value }))

// The canonical origin (not `globalThis.location`), so the value is identical on
// the server and during hydration and the social `<a href>`s below always share
// the public URL. Canonical and og:url are set site-wide by useSiteHead.
const siteUrl = SITE_URL
const pageUrl = computed(() => canonicalUrl(route.path))
const ogImage = computed(() => {
  const img = article.value?.meta?.image
  return `${siteUrl}${img || SHARE_IMAGE}`
})

// BlogPosting + breadcrumb structured data, so search engines and AI tools can read
// the post's headline, date and author, and where it sits on the site.
const blogPostingScripts = computed(() => {
  const post = article.value
  if (!post) return []
  return [
    jsonLdScript(
      'blogposting-jsonld',
      articleJsonLd({
        type: 'BlogPosting',
        headline: post.title,
        description: description.value,
        url: pageUrl.value,
        image: ogImage.value,
        datePublished: post.meta?.date,
        dateModified: post.meta?.updated,
        author: post.meta?.author,
        section: post.meta?.category,
        tags: post.meta?.tags,
      })
    ),
    jsonLdScript(
      'breadcrumb-jsonld',
      breadcrumbJsonLd([
        { name: 'Blog', path: '/blog' },
        { name: post.title, path: route.path },
      ])
    ),
  ]
})

useHead({
  script: blogPostingScripts,
  meta: [
    { property: 'og:type', content: 'article' },
    { property: 'og:image', content: ogImage },
    ...(article.value?.meta?.date ? [{ property: 'article:published_time', content: article.value.meta.date }] : []),
    ...(article.value?.meta?.tags || []).map((tag: string) => ({
      property: 'article:tag',
      content: tag,
    })),
  ],
})

function goBack() {
  navigateTo('/blog')
}
</script>

<template>
  <div v-if="article" class="article-page">
    <v-container class="pt-8">
      <v-row justify="center">
        <v-col cols="12" md="10" lg="8">
          <div class="d-flex align-center mb-6">
            <v-btn prepend-icon="$arrowLeft" variant="text" class="me-2" @click="goBack">
              <span class="text-body-2">Back to Blog</span>
            </v-btn>
          </div>

          <h1 class="text-h3 font-weight-bold mb-4">{{ article.title }}</h1>

          <div class="d-flex align-center flex-wrap mb-6">
            <div class="me-4 mb-2">
              <span class="text-body-2 text-medium-emphasis">{{
                articleReadingTime(articleBodyToString(article))
              }}</span>
            </div>

            <div>
              <v-chip
                v-if="article.meta?.category"
                size="small"
                :color="articleCategoryColor(article.meta.category)"
                class="me-2 mb-2"
              >
                {{ article.meta.category }}
              </v-chip>
              <v-chip v-for="tag in article.meta?.tags" :key="tag" size="x-small" variant="outlined" class="me-2 mb-2">
                {{ tag }}
              </v-chip>
            </div>
          </div>

          <ArticleImage
            :src="article.meta?.image"
            :alt="article.title"
            :seed="article.title"
            :base-color="articleCategoryBg(article.meta?.category)"
            :variant="articleCategoryVariant(article.meta?.category)"
            :fetch-width="1200"
            :fetch-height="675"
            aspect-ratio="16 / 9"
            eager
            class="rounded mb-6"
          />

          <v-divider class="mb-6" />
        </v-col>
      </v-row>

      <v-row justify="center">
        <v-col cols="12" md="10" lg="8">
          <div class="article-content">
            <ContentRenderer :value="article" />
          </div>

          <v-divider class="my-6" />
          <!-- <ShareButtons :title="article.title" :text="description" :url="pageUrl" class="mb-8" /> -->

          <PageFeedback :title="article.title" context="Blog post" question="Was this article helpful?" />

          <div v-if="article.meta?.author" class="author-section mb-8">
            <h3 class="text-h6 mb-3">About the Author</h3>
            <div class="d-flex align-center">
              <v-avatar color="primary" class="me-4">
                <span class="text-h6 text-white">{{ article.meta.author.charAt(0) }}</span>
              </v-avatar>
              <div>
                <div class="text-subtitle-1 font-weight-bold">{{ article.meta.author }}</div>
                <!-- <div class="text-body-2 text-medium-emphasis">Song7 Team</div> -->
              </div>
            </div>
          </div>

          <div v-if="relatedArticles && relatedArticles.length" class="related-section">
            <h2 class="text-h5 font-weight-bold mb-6">You might also like</h2>
            <v-row>
              <v-col v-for="related in relatedArticles" :key="related.path" cols="12" sm="6">
                <ArticleCard :post="related" />
              </v-col>
            </v-row>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </div>
  <div v-else class="d-flex justify-center align-center" style="min-height: 500px">
    <v-progress-circular indeterminate color="primary" />
  </div>
</template>
